/*
  lx-carousel — dependency-free auto-advancing slider.
  Replaces GemPages gp-carousel-v7-5.js for the rebuilt sections.

  Usage:
    <lx-carousel data-interval="2000" data-speed="500" data-easing="ease-out"
                 data-media="(max-width: 767px)" data-pause-hover="true">
      <div data-lx-track> ...slides... </div>
    </lx-carousel>

  - data-media: only runs while the media query matches (grid fallback outside it).
  - Seamless infinite loop via cloned leading slides.
  - Swipe/drag via pointer events. Pauses on hover when data-pause-hover="true".
  - Respects prefers-reduced-motion (no autoplay; swipe still works).

  Opt-in attributes (plan C7 — absent on the homepage hero/benefit consumers,
  whose behavior is unchanged):
  - data-autoplay="false": no timer; advance only via arrows/dots/drag
    (product similar-styles carousel — spec 05: autoplay false, loop true).
  - data-fractional="true": slide widths are fractional per-view peek layouts
    (e.g. 4.5/2.5/1.5-up); clone count uses Math.ceil(perView) so the loop
    seam is always covered by clones.
  Clones are stripped of [id] attributes so slides containing id'd markup
  (e.g. quick-add modals/openers) never duplicate ids.
*/
if (!customElements.get('lx-carousel')) {
class LxCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-lx-track]');
    if (!this.track) return;
    this.interval = parseInt(this.dataset.interval || '2000', 10);
    this.speed = parseInt(this.dataset.speed || '500', 10);
    this.easing = this.dataset.easing || 'ease-out';
    this.pauseHover = this.dataset.pauseHover !== 'false';
    this.autoplay = this.dataset.autoplay !== 'false';
    this.slides = Array.from(this.track.children);
    this.index = 0;
    this.active = false;
    this.mq = this.dataset.media ? window.matchMedia(this.dataset.media) : null;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    this._evaluate = this.evaluate.bind(this);
    this._onResize = this.onResize.bind(this);
    if (this.mq) this.mq.addEventListener('change', this._evaluate);
    window.addEventListener('resize', this._onResize);

    this.bindDrag();
    this.bindControls();
    this.evaluate();
  }

  bindControls() {
    this.querySelectorAll('[data-lx-prev]').forEach((b) =>
      b.addEventListener('click', () => {
        this.stopTimer();
        this.prev();
        this.startTimer();
      })
    );
    this.querySelectorAll('[data-lx-next]').forEach((b) =>
      b.addEventListener('click', () => {
        this.stopTimer();
        this.next();
        this.startTimer();
      })
    );
    const dotsHost = this.querySelector('[data-lx-dots]');
    if (dotsHost) {
      this.dots = this.slides.map((_, i) => {
        const d = document.createElement('button');
        d.type = 'button';
        d.className = 'lx-carousel-dot';
        d.setAttribute('aria-label', `${(window.lxI18n && window.lxI18n.slide) || 'Slide'} ${i + 1}`);
        d.addEventListener('click', () => {
          if (this._snapPending) return;
          this.stopTimer();
          this.goTo(i, true);
          this.updateDots();
          this.startTimer();
        });
        dotsHost.appendChild(d);
        return d;
      });
      this.updateDots();
    }
  }

  updateDots() {
    if (!this.dots) return;
    const current = this.index % this.slides.length;
    this.dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
  }

  disconnectedCallback() {
    this.stop();
    if (this.mq) this.mq.removeEventListener('change', this._evaluate);
    window.removeEventListener('resize', this._onResize);
  }

  evaluate() {
    if (!this.mq || this.mq.matches) this.start();
    else this.stop();
  }

  slideWidth() {
    // Step includes inter-slide gap: distance between consecutive slide origins.
    if (this.slides.length > 1) {
      return this.slides[1].getBoundingClientRect().left - this.slides[0].getBoundingClientRect().left;
    }
    const first = this.slides[0];
    return first ? first.getBoundingClientRect().width : 0;
  }

  perView() {
    const w = this.slideWidth();
    if (!w) return 1;
    const ratio = this.track.clientWidth / w;
    // Fractional peek layouts (data-fractional): ceil so the loop seam is
    // fully covered by clones (e.g. 4.5-up measures 4.43 → 5 clones needed).
    if (this.dataset.fractional === 'true') return Math.max(1, Math.ceil(ratio - 0.001));
    return Math.max(1, Math.round(ratio));
  }

  syncClones() {
    // Appends clones until the loop seam is covered at the current breakpoint.
    // Called at start() and on resize (breakpoint changes can raise the need).
    const need = Math.min(this.perView(), this.slides.length);
    while (this.clones.length < need) {
      const c = this.slides[this.clones.length].cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.dataset.lxClone = '';
      c.removeAttribute('id');
      c.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      this.track.appendChild(c);
      this.clones.push(c);
    }
  }

  start() {
    if (this.active) return;
    this.active = true;
    this.clones = [];
    this.syncClones();
    this.startTimer();
    if (this.pauseHover) {
      this._pause = () => this.stopTimer();
      this._resume = () => this.startTimer();
      this.addEventListener('mouseenter', this._pause);
      this.addEventListener('mouseleave', this._resume);
    }
  }

  stop() {
    if (!this.active) return;
    this.active = false;
    this.stopTimer();
    (this.clones || []).forEach((c) => c.remove());
    this.clones = [];
    this.index = 0;
    this.track.style.transition = 'none';
    this.track.style.transform = '';
    if (this._pause) {
      this.removeEventListener('mouseenter', this._pause);
      this.removeEventListener('mouseleave', this._resume);
    }
  }

  startTimer() {
    if (!this.autoplay || this.reduced.matches) return;
    this.stopTimer();
    this.timer = setInterval(() => this.next(), this.interval);
  }

  stopTimer() {
    clearInterval(this.timer);
    this.timer = null;
  }

  goTo(index, animate) {
    this.index = index;
    const w = this.slideWidth();
    this.track.style.transition = animate ? `transform ${this.speed}ms ${this.easing}` : 'none';
    this.track.style.transform = `translateX(${-this.index * w}px)`;
    this.updateDots();
  }

  next() {
    if (this._snapPending) return;
    this.goTo(this.index + 1, true);
    if (this.index >= this.slides.length) {
      this._snapPending = true;
      setTimeout(() => {
        this._snapPending = false;
        if (this.active) this.goTo(0, false);
      }, this.speed + 30);
    }
  }

  prev() {
    if (this._snapPending) return;
    if (this.index <= 0) {
      // jump to the cloned end, then animate back one
      this.goTo(this.slides.length, false);
      requestAnimationFrame(() => this.goTo(this.slides.length - 1, true));
    } else {
      this.goTo(this.index - 1, true);
    }
  }

  onResize() {
    if (!this.active) return;
    this.syncClones();
    this.goTo(Math.min(this.index, this.slides.length), false);
  }

  bindDrag() {
    let startX = null;
    let baseOffset = 0;
    let dragging = false;
    this.track.addEventListener('pointerdown', (e) => {
      if (!this.active) return;
      startX = e.clientX;
      baseOffset = -this.index * this.slideWidth();
      dragging = false;
    });
    this.track.addEventListener('pointermove', (e) => {
      if (startX === null || !this.active) return;
      const dx = e.clientX - startX;
      if (!dragging && Math.abs(dx) > 5) {
        // Capture (and pause the timer) only once an intentional drag starts:
        // capturing on pointerdown retargets the resulting click to the
        // track, which would swallow taps on links/buttons inside the slides.
        // Capture guarantees this drag ends with a pointerup/cancel on the
        // track, so the timer stop below is always paired with a restart.
        this.track.setPointerCapture(e.pointerId);
        this.stopTimer();
        this.track.style.transition = 'none';
        dragging = true;
      }
      if (dragging) this.track.style.transform = `translateX(${baseOffset + dx}px)`;
    });
    const end = (e) => {
      if (startX === null || !this.active) return;
      const dx = e.clientX - startX;
      startX = null;
      if (dragging) {
        dragging = false;
        const threshold = this.slideWidth() / 4;
        if (dx < -threshold) this.next();
        else if (dx > threshold) this.prev();
        else this.goTo(this.index, true);
        this.startTimer();
      }
    };
    this.track.addEventListener('pointerup', end);
    this.track.addEventListener('pointercancel', end);
  }
}

customElements.define('lx-carousel', LxCarousel);
}
